import React, { ReactComponentElement } from 'react'
import { Icon } from 'pivotal-ui/react/iconography'
import * as Constants from '../constants/constants'
import { TKG_SLUG } from '../constants/constants'
import { ChosenProduct, permittedOutOfSupportProductSlugs } from './chosen_products_types'

export const NoLongerSupportedWarning = ({name}: Partial<ChosenProduct>) => (
  <div className='man type-sm'>
    <Icon src="warning" verticalAlign="baseline" className='fill-decorative-yellow' /> {name} is no longer supported and has been removed from your plan. Please choose a supported product before upgrading.
  </div>
)

export const NoPublicReleasesWarning = () => (
  <div className='man type-sm'>
    <Icon src="warning" verticalAlign="baseline" className='fill-decorative-yellow' />  All releases for this product are admins-only and cannot be downloaded at this time. Upgrade Planner does not currently support products without any publicly-available releases.
  </div>
)

export const SlugNotFoundWarning = ({name, slug}: Partial<ChosenProduct>) => (
  <div className='man type-sm'>
    <Icon src="warning" verticalAlign="baseline" className='fill-decorative-yellow' /> The product '{name || slug}' was not recognized. If this is a capability provided by Broadcom, please verify the name or add it manually below. If it's a custom capability, ensure it is fully compatible with your foundation's target state.
  </div>
)

export const BetaProductWarning = ({name}: Partial<ChosenProduct>) => (
  <div className='man type-sm'>
    <Icon src="warning" verticalAlign="baseline" className='fill-decorative-yellow' /> {name} is currently a beta product and isn't supported by the Upgrade Planner. Please remove this product from your selections to continue or consult its documentation for manual upgrade guidance.
  </div>
)

export const AddonProductWarning = ({name}: Partial<ChosenProduct>) => (
  <div className='man type-sm'>
    <Icon src="warning" verticalAlign="baseline" className='fill-decorative-yellow' /> {name} is an add-on and isn't directly supported by the Upgrade Planner. Please remove it from the list to generate your plan and review the product's release notes for separate installation or upgrade instructions.
  </div>
)

export const GeneralUnsupportedWarning = ({name}: Partial<ChosenProduct>) => (
  <div className='man type-sm'>
    <Icon src="warning" verticalAlign="baseline" className='fill-decorative-yellow' /> {name} is not currently supported by the Upgrade Planner. To proceed, please remove this product from your plan and manage its upgrade separately following the standard Broadcom documentation.
  </div>
)


type ProductDetailProps = {
  product: ChosenProduct,
  warningValue?: null | ReactComponentElement<any>
}

export const ProductDetail = ({product, warningValue = null}: ProductDetailProps) => {
  const rowText = product.name ? product.name : product.slug
  return (
    <div id={product.slug + '-row-label'}>
      <p className='man type-lg'>{rowText}</p>
      {warningValue}
    </div>
  )
}

// There is probably a better place for this
function calculateWarningValue(product: ChosenProduct): null | ReactComponentElement<any> {
  const { lifecycleState, slug, name } = product

  if(product.slug === TKG_SLUG) {
    return <GeneralUnsupportedWarning name={product.name}/>
  }

  if(typeof(lifecycleState) !== 'undefined' && Constants.OUT_OF_SUPPORT_RELEASE_LIFECYCLE_LIST.includes(lifecycleState) && !permittedOutOfSupportProductSlugs.includes(product.slug)) {
    return <NoLongerSupportedWarning name={name}/>
  }

  if (product.failedToFetch) {
    return <SlugNotFoundWarning slug={slug} name={name}/>
  }

  if (lifecycleState === Constants.BETA_LIFECYCLE_STATE) {
    return <BetaProductWarning name={name}/>
  }

  if (Constants.ADDON_SLUGS.includes(product.slug)) {
    return <AddonProductWarning name={name}/>
  }

  if (product.releases === undefined) {
    return null
  }

  if (product.releases.length === 0) {
    return <NoPublicReleasesWarning/>
  }

  return null
}

type WrappedProductDetailProps = {
  product: ChosenProduct
}

const WrappedProductDetail = ({product}: WrappedProductDetailProps) => (
  <ProductDetail product={product} warningValue={calculateWarningValue(product)}/>
)

export default WrappedProductDetail
